<template>
  <div id="application-rules__table">
    <v-flex d-flex>
      <v-subheader class="font-weight-bold">
        <span id="application-rules__rows">
          <span id="application-rules__total-string">Total: </span>
          <span id="application-rules__rows-count">{{ rowsCount }}</span>
        </span>
      </v-subheader>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        :label="$t('actions.search')"
        class="mx-2"
        prepend-icon="mdi-magnify"
        single-line
        clearable
        id="application-rules__table-search"
      ></v-text-field>
    </v-flex>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      class="elevation-1 mx-2 scroll"
      :loading="loading"
      hide-default-footer
      :options.sync="options"
      style="min-height: 100%;"
      id="application-rules__table"
      must-sort
      fixed-header
      :height="loadItems.length > 5 ? height - 15 + 'px' : '100%'"
    >
      <template v-slot:[`body.append`]>
        <td
          colspan="3"
          class="text-center py-2"
          v-if="options.itemsPerPage < loadItems.length"
        >
          <v-progress-circular
            :size="30"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </td>
      </template>
      <template
        v-slot:[`item.vpn_profile_name`]="props"
        v-if="isPermitted(PermissionEnum.OPENVPN_PROFILE.VIEW)"
      >
        <span>{{ props.item.vpn_profile_name }}</span>
      </template>
      <template
        v-slot:[`item.is_vpn_profile_enabled`]="props"
        v-if="isPermitted(PermissionEnum.OPENVPN_PROFILE.VIEW)"
      >
        <v-simple-checkbox
          readonly
          disabled
          color="primary"
          hide-details
          v-model="props.item.is_vpn_profile_enabled"
          class="align-center justify-center"
          :id="'application-rules__table-is_vpn_profile_enabled' + props.index"
        ></v-simple-checkbox>
      </template>
      <template v-slot:[`item.is_enabled`]="props">
        <v-simple-checkbox
          readonly
          disabled
          color="primary"
          hide-details
          v-model="props.item.is_enabled"
          class="align-center justify-center"
          :id="'application-rules__table-is_enabled-' + props.index"
        ></v-simple-checkbox>
      </template>
      <template v-slot:[`item.action`]="props">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              class="mr-6"
              @click="editItem(props.item)"
              :disabled="!isPermitted(PermissionEnum.APPLICATION_RULES.UPDATE)"
              :id="'application-rules__edit-' + props.index"
              v-on="on"
              v-bind="attrs"
              >mdi-pencil</v-icon
            >
          </template>
          <span>{{ $t("actions.edit") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="deleteItem(props.item)"
              :id="'application-rules__delete-' + props.index"
              :disabled="!isPermitted(PermissionEnum.APPLICATION_RULES.DELETE)"
              v-on="on"
              v-bind="attrs"
              >mdi-delete</v-icon
            >
          </template>
          <span>{{ $t("actions.delete") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import { permissionMixin } from "@/mixins/permissions";

export default {
  name: "ApplicationRulesTable",
  mixins: [permissionMixin],
  data() {
    return {
      height: 0,
      rows: 20,
      loadItems: [],
      options: {},
      loading: false,
      headers: [
        {
          text: this.$t("modules.dataPath.name"),
          align: "left",
          value: "name"
        },
        {
          text: this.$t("modules.dataPath.vpnProfile"),
          align: "left",
          value: "vpn_profile_name"
        },
        {
          text: this.$t("modules.dataPath.vpnProfileEnabled"),
          align: "center",
          value: "is_vpn_profile_enabled",
          width: "15%"
        },
        {
          text: this.$t("modules.dataPath.applicationEnabled"),
          align: "center",
          value: "is_enabled"
        },
        {
          text: this.$t("actions.actions"),
          align: "center",
          value: "action",
          sortable: false,
          width: "15%"
        }
      ],
      editedIndex: -1
    };
  },

  computed: {
    vpnProfiles() {
      return this.$store.state.dataPath.vpnProfile.vpnProfiles;
    },
    dialog: {
      get() {
        return this.$store.state.dataPath.dialog;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleDialog", val);
      }
    },
    search: {
      get() {
        return this.$store.state.dataPath.search || "";
      },
      set(val) {
        this.$store.commit("dataPath/updateSearch", val);
      }
    },
    applicationRules() {
      return this.$store.state.dataPath.applicationRules.map(data => {
        let vpnProfiles = this.vpnProfiles.filter(item => {
          return data.profile_id === item.id;
        });
        vpnProfiles.map(item => {
          data.vpn_profile_name = item.name;
          data.is_vpn_profile_enabled = item.is_enabled;
        });
        return data;
      });
    },
    editedItem() {
      return this.$store.state.dataPath.editedItem;
    },
    rowsCount() {
      return this.loadItems.length;
    }
  },
  created() {
    this.initialize();
  },
  beforeDestroy() {
    document
      .querySelector("#application-rules__table .v-data-table__wrapper")
      .removeEventListener("scroll", this.scroll);
    this.search = "";
  },
  mounted() {
    let el = document.querySelector(
      "#application-rules__table .v-data-table__wrapper"
    );
    el.addEventListener("scroll", this.scroll);
    this.computeHeight();
  },

  watch: {
    search(val) {
      this.searchItems(val);
    },
    applicationRules() {
      if (!this.search) {
        this.loadItems = this.applicationRules;
      } else {
        this.searchItems(this.search);
      }
    }
  },

  methods: {
    scroll({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (this.options.itemsPerPage < this.applicationRules.length) {
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          this.paginate();
        }
      }
    },
    computeHeight() {
      let el = this.$refs.dataContainer.$el;
      let boxOffset = el.getBoundingClientRect();
      let viewPortHeight = window.innerHeight;
      this.height = viewPortHeight - boxOffset.top;
      this.height = this.height < 300 ? 300 : this.height;
    },
    paginate() {
      this.options.itemsPerPage = this.options.itemsPerPage + this.rows;
    },
    initialize() {
      this.options.itemsPerPage = this.rows;
      this.loading = true;
      this.$store
        .dispatch("dataPath/fetchAll")
        .then(() => {
          if (this.search) {
            this.searchItems(this.search);
          } else {
            this.loadItems = this.applicationRules;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchItems(val) {
      this.loadItems = this.applicationRules.filter(o =>
        this.headers
          .filter(item => item.value !== "action")
          .map(item => item.value)
          .some(k => {
            if (o[k]) {
              return (
                o[k]
                  .toString()
                  .toLowerCase()
                  .indexOf(val.toLowerCase()) > -1
              );
            }
          })
      );
    },

    editItem(item) {
      let editedIndex = this.applicationRules.indexOf(item);
      this.$store.commit("dataPath/updateFormTitle", 2);
      this.$store.commit("dataPath/updateIndex", editedIndex);
      this.$store.dispatch("dataPath/editItem", item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.loading = true;
      let confirmed = confirm(this.$t("modules.dataPath.confirmDelete"));
      if (confirmed) {
        this.$store
          .dispatch("dataPath/delete", item.id)
          .catch(error => {
            handleError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    }
  }
};
</script>
